import {
  buildQueries,
  getDefaultNormalizer,
  queries,
  queryHelpers,
  within,
} from '@testing-library/react'

/**
 * Patching within() return types to support multiple arguments
 * https://github.com/testing-library/dom-testing-library/blob/main/types/get-queries-for-element.d.ts
 */
export type BoundFunction<T> = T extends (
  container: HTMLElement,
  ...args: infer P
) => infer R
  ? (...args: P) => R
  : never

export type BoundFunctions<T> = { [P in keyof T]: BoundFunction<T[P]> }

export type Query = (
  container: HTMLElement,
  ...args: any[]
) =>
  | Error
  | HTMLElement
  | HTMLElement[]
  | Promise<HTMLElement[]>
  | Promise<HTMLElement>
  | null

export interface Queries {
  [T: string]: Query
}

const normalizer = getDefaultNormalizer({
  trim: true,
  collapseWhitespace: false,
})

/**
 * Query header row by index
 */

export const queryAllHeaderRowByIndex = (
  container: HTMLElement,
  index: number
) => {
  const row = container.querySelectorAll('thead tr')[index] as HTMLElement

  if (row) {
    return [row]
  } else {
    return []
  }
}

const getHeaderRowMultipleError = () =>
  `Found multiple header rows. (This should never happen)`
const getHeaderRowMissingError = (c, rowIndex) =>
  `Unable to find the ${rowIndex}-th header row`

const [
  queryHeaderRowByIndex,
  getAllHeaderRowByIndex,
  getHeaderRowByIndex,
  findAllHeaderRowByIndex,
  findHeaderRowByIndex,
] = buildQueries(
  queryAllHeaderRowByIndex,
  getHeaderRowMultipleError,
  getHeaderRowMissingError
)

/**
 * Query body row by index
 */

export const queryAllBodyRowByIndex = (
  container: HTMLElement,
  index: number
) => {
  const row = container.querySelectorAll('tbody tr')[index] as HTMLElement

  if (row) {
    return [row]
  } else {
    return []
  }
}

const getBodyRowMultipleError = () =>
  `Found multiple body rows. (This should never happen)`
const getBodyRowMissingError = (c, rowIndex) =>
  `Unable to find the ${rowIndex}-th body row`

const [
  queryBodyRowByIndex,
  getAllBodyRowByIndex,
  getBodyRowByIndex,
  findAllBodyRowByIndex,
  findBodyRowByIndex,
] = buildQueries(
  queryAllBodyRowByIndex,
  getBodyRowMultipleError,
  getBodyRowMissingError
)

/**
 * Query <th> by Text
 */

export const queryAllHeaderCellByText = (
  container: HTMLElement,
  rowIndex: number,
  text: string
) => {
  try {
    return within(getHeaderRowByIndex(container, rowIndex)).getAllByText(
      (_, element) => {
        return (
          element.tagName.toLowerCase() === 'th' &&
          normalizer(element.textContent) === text
        )
      }
    )
  } catch {
    return []
  }
}

const getTHMultipleError = (c, rowIndex, queryText) =>
  `Found multiple header cells with the text of "${queryText}" in the ${rowIndex}-nth header row`

const getTHMissingError = (c, rowIndex, queryText) =>
  `Unable to find a header cell with the text of "${queryText}" in the ${rowIndex}-nth header row`

const [
  queryHeaderCellByText,
  getAllHeaderCellByText,
  getHeaderCellByText,
  findAllHeaderCellByText,
  findHeaderCellByText,
] = buildQueries(
  queryAllHeaderCellByText,
  getTHMultipleError,
  getTHMissingError
)

/**
 * Query <td> by Text
 */

export const queryAllBodyCellByText = (
  container: HTMLElement,
  rowIndex: number,
  text: string
) => {
  try {
    return within(getBodyRowByIndex(container, rowIndex)).getAllByText(
      (_, element) => {
        return (
          element.tagName.toLowerCase() === 'td' &&
          normalizer(element.textContent) === text
        )
      }
    )
  } catch {
    return []
  }
}

const getTDMultipleError = (c, rowIndex, queryText) =>
  `Found multiple body cells with the text of "${queryText}" in the ${rowIndex}-nth body row`

const getTDMissingError = (c, rowIndex, queryText) =>
  `Unable to find a body cell with the text of "${queryText}" in the ${rowIndex}-nth body row`

const [
  queryBodyCellByText,
  getAllBodyCellByText,
  getBodyCellByText,
  findAllBodyCellByText,
  findBodyCellByText,
] = buildQueries(queryAllBodyCellByText, getTDMultipleError, getTDMissingError)

/**
 * Query header cell by index
 */

export const queryAllHeaderCellByIndex = (
  container: HTMLElement,
  rowIndex: number,
  cellIndex: number
) => {
  try {
    const cell = getHeaderRowByIndex(container, rowIndex).querySelectorAll(
      'th'
    )[cellIndex]

    return cell ? [cell] : []
  } catch {
    return []
  }
}

const getHeaderCellByIndexMultipleError = (c, rowIndex, queryText) => ``
const getHeaderCellByIndexMissingError = (c, rowIndex, columnIndex) =>
  `Unable to find the ${columnIndex}-nth header cell in the ${rowIndex}-nth header row`

const [
  queryHeaderCellByIndex,
  getAllHeaderCellByIndex,
  getHeaderCellByIndex,
  findAllHeaderCellByIndex,
  findHeaderCellByIndex,
] = buildQueries(
  queryAllHeaderCellByIndex,
  getHeaderCellByIndexMultipleError,
  getHeaderCellByIndexMissingError
)

/**
 * Query body cell by index
 */

export const queryAllBodyCellByIndex = (
  container: HTMLElement,
  rowIndex: number,
  cellIndex: number
) => {
  try {
    const cell = getBodyRowByIndex(container, rowIndex).querySelectorAll('td')[
      cellIndex
    ]

    return cell ? [cell] : []
  } catch {
    return []
  }
}

const getBodyCellByIndexMultipleError = (c, rowIndex, queryText) => ``
const getBodyCellByIndexMissingError = (c, rowIndex, columnIndex) =>
  `Unable to find the ${columnIndex}-nth body cell in the ${rowIndex}-nth body row`

const [
  queryBodyCellByIndex,
  getAllBodyCellByIndex,
  getBodyCellByIndex,
  findAllBodyCellByIndex,
  findBodyCellByIndex,
] = buildQueries(
  queryAllBodyCellByIndex,
  getBodyCellByIndexMultipleError,
  getBodyCellByIndexMissingError
)

const tableQueries = {
  // getRowByIndex Queries
  getHeaderRowByIndex,
  queryHeaderRowByIndex,
  findHeaderRowByIndex,
  getBodyRowByIndex,
  queryBodyRowByIndex,
  findBodyRowByIndex,
  // getCellByText Queries
  queryHeaderCellByText,
  getAllHeaderCellByText,
  getHeaderCellByText,
  findAllHeaderCellByText,
  findHeaderCellByText,
  queryBodyCellByText,
  getAllBodyCellByText,
  getBodyCellByText,
  findAllBodyCellByText,
  findBodyCellByText,
  // getCellByIndex
  queryHeaderCellByIndex,
  getHeaderCellByIndex,
  findHeaderCellByIndex,
  // getCellByIndex
  queryBodyCellByIndex,
  getBodyCellByIndex,
  findBodyCellByIndex,
}

export default tableQueries

/**
 * Within
 */

export const withinTable = (
  table: HTMLElement
): BoundFunctions<typeof tableQueries> => {
  if (!table || table.tagName.toLowerCase() !== 'table') {
    throw queryHelpers.getElementError(
      'Element received is not a <table /> element',
      table
    )
  }

  return within(table, {
    ...queries,
    ...tableQueries,
  })
}
